import React, { useState, useRef, useEffect } from 'react';

type PromptType = 'auto' | 'process' | 'wireframe' | 'document';

interface PromptBarProps {
  projectId: string;
  onArtifactCreated: () => void;
  claudeAvailable: boolean;
  onGeneratingChange?: (generating: boolean) => void;
}

const TYPE_OPTIONS: Array<{ value: PromptType; label: string; hint: string }> = [
  { value: 'auto', label: 'Auto', hint: 'Claude entscheidet' },
  { value: 'process', label: 'Prozess', hint: 'Mermaid-Flowchart' },
  { value: 'wireframe', label: 'Wireframe', hint: 'HTML/CSS Mockup' },
  { value: 'document', label: 'Dokument', hint: 'Markdown' },
];

const PromptBar: React.FC<PromptBarProps> = ({ projectId, onArtifactCreated, claudeAvailable, onGeneratingChange }) => {
  const [prompt, setPrompt] = useState('');
  const [type, setType] = useState<PromptType>('auto');
  const [loading, setLoading] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [showStream, setShowStream] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 200) + 'px';
    }
  }, [prompt]);

  // Warn before closing during generation
  useEffect(() => {
    if (!loading) return;
    const handler = (e: BeforeUnloadEvent) => { e.preventDefault(); };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [loading]);

  // Notify parent of generation state
  useEffect(() => {
    onGeneratingChange?.(loading);
  }, [loading, onGeneratingChange]);

  const handleGenerate = async () => {
    if (!prompt.trim() || loading) return;

    setLoading(true);
    setStatusMsg('Claude CLI wird gestartet...');
    setError(null);
    setShowStream(true);

    try {
      const response = await fetch('/api/studio/generate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: prompt.trim(), type, projectId }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Anfrage fehlgeschlagen');
      }

      const reader = response.body!.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'status') {
              setStatusMsg(data.message);
            } else if (data.type === 'chunk') {
              setStatusMsg(`Claude generiert...`);
            } else if (data.type === 'done') {
              setStatusMsg('Fertig! Artefakt erstellt.');
              setPrompt('');
              onArtifactCreated();
              setTimeout(() => {
                setShowStream(false);
                setStatusMsg('');
              }, 1500);
            } else if (data.type === 'error') {
              throw new Error(data.message);
            }
          } catch (e: unknown) {
            if (e instanceof Error && !e.message.includes('Unexpected')) {
              throw e;
            }
          }
        }
      }
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Unbekannter Fehler');
      setStatusMsg('');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      handleGenerate();
    }
  };

  if (!claudeAvailable) {
    return (
      <div className="studio-prompt-bar studio-prompt-unavailable">
        <div className="studio-prompt-unavailable-msg">
          <strong>Claude CLI nicht verfuegbar</strong>
          <span>Installiere Claude Code oder nutze die MCP-Tools direkt in deinem Terminal.</span>
        </div>
      </div>
    );
  }

  return (
    <div className={`studio-prompt-bar ${loading ? 'generating' : ''}`}>
      <div className="studio-prompt-type-row">
        {TYPE_OPTIONS.map(opt => (
          <button
            key={opt.value}
            className={`studio-prompt-type-btn ${type === opt.value ? 'active' : ''}`}
            onClick={() => setType(opt.value)}
            disabled={loading}
            title={opt.hint}
          >
            {opt.label}
          </button>
        ))}
      </div>

      <div className="studio-prompt-input-row">
        <textarea
          ref={textareaRef}
          value={prompt}
          onChange={e => setPrompt(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={
            type === 'process' ? 'Beschreibe den Geschaeftsprozess...'
            : type === 'wireframe' ? 'Beschreibe die Seite / das UI...'
            : type === 'document' ? 'Beschreibe das Dokument...'
            : 'Was moechtest du erstellen? (Claude erkennt den Typ automatisch)'
          }
          disabled={loading}
          rows={1}
          className="studio-prompt-textarea"
        />
        <button
          className="studio-prompt-send-btn"
          onClick={handleGenerate}
          disabled={loading || !prompt.trim()}
          title="Ctrl+Enter"
        >
          {loading ? (
            <span className="studio-prompt-spinner" />
          ) : (
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M22 2L11 13" /><path d="M22 2L15 22L11 13L2 9L22 2Z" />
            </svg>
          )}
        </button>
      </div>

      {error && (
        <div className="studio-prompt-error">
          {error}
          <button onClick={() => setError(null)}>&times;</button>
        </div>
      )}

      {showStream && (
        <div className="studio-prompt-stream">
          <div className="studio-prompt-stream-header">
            <span className="studio-prompt-status">
              {loading && <span className="studio-prompt-dot-pulse" />}
              {statusMsg}
            </span>
            <button
              className="studio-prompt-stream-toggle"
              onClick={() => setShowStream(false)}
            >
              Ausblenden
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PromptBar;
